import algoliasearchHelper from 'algoliasearch-helper'
import algoliasearch from 'algoliasearch/lite'
import isEmpty from 'lodash.isempty'

// Docs: https://community.algolia.com/algoliasearch-helper-js/reference.html

const ATTRIBUTES_TO_RETRIEVE = [
  'objectID',
  'backmarketID',
  'link',
  'merchant_link',
  'price',
  'price_with_currency',
  'price_new',
  'price_new_with_currency',
  'warranty',
  'title',
  'title_model',
  'sub_title_elements',
  'sub_title',
  'image1',
  'compatible_carriers',
  'is_sim_locked',
  'list_view',
  'nb_results_starting_from',
  'stockRaw',
  'pressureStockLimit',
  'backbox_grade_label',
  'backbox_grade_value',
  'link_grade_v2',
  'merchant_link_grade_v2',
]

export const FACET_FILTER = 'facet_filter'
export const FACET_PRICE = 'price'
export const FACET_GRAPH = 'chart'
export const FACET_PAGE = 'page'
export const FACET_SORT = 'sort'
export const FACET_QUERY = 'q'
export const FACET_CARRIER = 'compatible_carriers_facet'
const PAGINATION = 30

export const normalizeFacetLabel = (label, sorted) => {
  if (sorted) {
    const [id, ...words] = label.split(' ')

    return {
      id: Number(id),
      label: words.join(' '),
    }
  }

  return {
    id: 0,
    label,
  }
}

export const orderFacets = (facets, manualSort = false) => {
  const facetsById = Object.entries(facets).reduce(
    (acc, [facetValue, count]) => {
      const { label, id } = normalizeFacetLabel(facetValue, manualSort)

      return [
        ...acc,
        {
          label,
          facet_value: facetValue,
          id,
          count,
        },
      ]
    },
    [],
  )

  facetsById.sort((a, b) => a.id - b.id)

  // The id is only necessary to sort
  return facetsById.map(({ id, ...facetWithoutId }) => facetWithoutId)
}

export const useAlgolia = ({
  ALGOLIA_ID,
  ALGOLIA_KEY,
  context,
  searchParams,
  attributesToRetrieve = [],
} = {}) =>
  new Promise((resolve, reject) => {
    const {
      page: searchPage,
      q,
      sort,
      price,
      ...activeFilters
    } = searchParams.toFilters()

    const facets = context.facets
      .filter((facet) => facet.facetType === FACET_FILTER)
      .map((facet) => facet.facetName)
    const facetsWhitelist = [...Object.keys(context.refinements), ...facets]

    const filtersWhitelisted = Object.entries(activeFilters).reduce(
      (acc, [name, values]) => {
        if (facetsWhitelist.includes(name)) {
          return { ...acc, [name]: values }
        }

        return acc
      },
      {},
    )

    const client = algoliasearch(ALGOLIA_ID, ALGOLIA_KEY)
    const indexObject = sort
      ? context.indexes.find((item) => item.value === sort[0]) || {}
      : context.indexes.find((item) => item.active) || {}

    const { queryParams, value: index } = indexObject

    const facetGraph = context.facets.find(
      (facet) => facet.displayType === FACET_GRAPH,
    )
    const scales = facetGraph
      ? facetGraph.scales.map((scale) => scale.facet)
      : []

    const facetPriceFilter = facetGraph ? facetGraph.facetName : FACET_PRICE

    const AND_FACETS = []
    const OR_FACETS = Object.entries(filtersWhitelisted)

    const AND_FACETS_NAME = [...facets, ...scales]
    //  The price has to be there, to be able to retrieve the stats
    const OR_FACETS_NAME = [
      ...Object.keys(filtersWhitelisted),
      facetPriceFilter,
    ]

    //  If there are multiple refinements on the same key, the key becomes a "OR" Facet
    Object.entries(context.refinements).forEach(
      ([refinementName, refinementValues]) => {
        if (refinementValues.length === 1) {
          AND_FACETS_NAME.push(refinementName)
          AND_FACETS.push([refinementName, refinementValues])
        } else {
          OR_FACETS_NAME.push(refinementName)
          OR_FACETS.push([refinementName, refinementValues])
        }
      },
    )

    const helper = algoliasearchHelper(client, index, {
      facets: AND_FACETS_NAME,
      disjunctiveFacets: OR_FACETS_NAME,
      ...queryParams,
      analyticsTags: searchParams.tags,
    })

    helper.clearCache()

    AND_FACETS.forEach(([refinementName, refinementValues]) => {
      helper.addFacetRefinement(refinementName, refinementValues[0])
    })

    OR_FACETS.forEach(([filterName, filterValues]) => {
      filterValues.forEach((filterValue) => {
        helper.addDisjunctiveFacetRefinement(filterName, filterValue)
      })
    })

    context.numericFilters.forEach(
      ([numericFilterKey, numericFilterType, numericFilterValue]) => {
        helper.addNumericRefinement(
          numericFilterKey,
          numericFilterType,
          numericFilterValue,
        )
      },
    )

    helper.setQueryParameter('hitsPerPage', PAGINATION)
    helper.setQueryParameter('attributesToRetrieve', [
      ...attributesToRetrieve,
      ...ATTRIBUTES_TO_RETRIEVE,
    ])

    if (q) {
      helper.setQuery(q)
    }

    if (!isEmpty(price)) {
      const prices = price.length > 1 ? price : [0, ...price]
      // apply min and max on price
      helper.addNumericRefinement(facetPriceFilter, '>=', Number(prices[0]))
      helper.addNumericRefinement(facetPriceFilter, '<=', Number(prices[1]))
    }

    // Algolia's pagination starts from 0
    helper.setPage(searchPage - 1)

    helper.on('result', ({ results }) => {
      const {
        disjunctiveFacets,
        hits,
        page,
        nbPages,
        nbHits,
        facets: facetsResponse,
        queryID,
      } = results
      // remove scale facets
      const mergedFacets = facetsResponse
        .filter((facet) => !scales.includes(facet.name))
        .map((facet) => {
          const hasDisjunctiveFacet = disjunctiveFacets.find(
            (f) => f.name === facet.name,
          )

          return hasDisjunctiveFacet || facet
        })

      const graphFacets = results.facets.filter((facet) =>
        scales.includes(facet.name),
      )

      const payload = {
        index,
        searchParams,
        hits,
        page,
        nbPages,
        nbHits,
        facets: [...mergedFacets, ...graphFacets],
        stats: results.getFacetStats(facetPriceFilter),
        called: true,
        queryID,
      }

      resolve(payload)
    })

    helper.on('error', (event) => {
      reject(event.error)
    })

    helper.search()
  })
